import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import DistributionForm from "./DistributionForm";
import ReviewForm from "./ReviewForm";
import "./AddAutovehicles.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Select from 'react-select';

const ViewAutovehicles = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // tipuri combustibil
    const fuelTypes = [
        "Benzina",
        "Motorina",
        "GPL",
        "Hybrid",
        "Electric",
    ];

    // formular - fără expire aici (mutat în contract)
    const initialFormData = {
        auto: "",
        auto_type: "",
        driver: "",
        registration_number: "",
        RCA: "",
        RCA_display: "",
        ITP: "",
        ITP_display: "",
        E_Vignette: "",
        E_Vignette_display: "",
        VIN_series: "",
        year: "",
        cylindrical_capacity: "",
        combustibile: "",
    };

    // state-uri principale
    const [formData, setFormData] = useState(initialFormData);
    const [drivers, setDrivers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);

    // State pentru validare
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Refs pentru a ține minte valorile inițiale
    const initialKilometersRef = useRef(null);
    const initialDistributionRef = useRef(null);
    const initialReviewRef = useRef(null);

    // state pentru distribuție și revizie
    const [distribution, setDistribution] = useState({
        distribution_data: "",
        distribution_km: "",
        distribution_interval: "",
        estimate_date_distribution: "",
        estimate_km_distribution: "",
    });

    const [review, setReview] = useState({
        review_data: "",
        review_km: "",
        review_interval: "",
        estimate_date_review: "",
        estimate_km_review: "",
    });

    // state pentru kilometraj
    const [kilometers, setKilometers] = useState({
        kilometers: "",
        recorded_date: format(new Date(), "yyyy-MM-dd"),
        recorded_date_display: format(new Date(), "dd.MM.yyyy"),
    });

    // Stiluri personalizate pentru react-select
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: '100%',
            height: '38px',
            border: state.isFocused ? '2px solid #050d15' : '1.5px solid #ccc',
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(0, 0, 0, 0.2)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#050d15' : '1.5px solid #ccc',
            },
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            minHeight: '38px',
            cursor: 'pointer',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#777',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#1a1a1a',
            fontFamily: "'Arial', sans-serif",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '36px',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#1976d2' : '#555',
            padding: '8px',
            '&:hover': {
                color: '#1976d2',
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 9999,
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px 0',
            maxHeight: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#1976d2' :
                state.isFocused ? '#f0f7ff' : 'white',
            color: state.isSelected ? 'white' : '#1a1a1a',
            padding: '8px 12px',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            cursor: 'pointer',
            '&:active': {
                backgroundColor: '#1976d2',
                color: 'white'
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#666',
            padding: '8px',
            '&:hover': {
                color: '#333'
            }
        }),
    };

    // Stil pentru select-uri goale
    const emptySelectStyles = {
        ...customSelectStyles,
        singleValue: (provided) => ({
            ...provided,
            color: '#777 !important',
        }),
    };

    // Stil pentru select-uri cu eroare
    const errorSelectStyles = {
        ...customSelectStyles,
        control: (provided, state) => ({
            ...customSelectStyles.control(provided, state),
            border: '1px solid #ff5c5c !important',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 92, 92, 0.2)' : '0 0 0 1px rgba(255, 92, 92, 0.5)',
            '&:hover': {
                borderColor: '#ff5c5c !important',
            }
        })
    };

    // Opțiuni pentru react-select
    const driverOptions = drivers.map(driver => ({
        value: driver.id,
        label: `${driver.first_name} ${driver.last_name}`
    }));

    const fuelOptions = fuelTypes.map(fuel => ({
        value: fuel,
        label: fuel
    }));

    // Handler pentru react-select pentru driver
    const handleDriverSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            driver: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, driver: "" }));
        }
    };

    // Handler pentru react-select pentru combustibil
    const handleFuelSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            combustibile: selectedOption ? selectedOption.value : ""
        }));
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, combustibile: "" }));
        }
    };

    // Găsește valoarea selectată pentru driver
    const selectedDriver = driverOptions.find(option => option.value === formData.driver);
    // Găsește valoarea selectată pentru combustibil
    const selectedFuel = fuelOptions.find(option => option.value === formData.combustibile);

    // încarcă datele mașinii
    useEffect(() => {
        const load = async () => {
            try {
                console.log("=== LOADING VEHICLE DATA FOR VIEW ===");

                const [autoResponse, driversResponse, allKmResponse] = await Promise.all([
                    axiosInstance.get(`/all-autovehicles/${id}/`),
                    axiosInstance.get("/employees/"),
                    axiosInstance.get("/autovehicle-kilometers/")
                ]);

                const [allDistResponse, allRevResponse] = await Promise.all([
                    axiosInstance.get("/distribution/"),
                    axiosInstance.get("/review/")
                ]);

                setDrivers(driversResponse.data);

                const d = autoResponse.data;

                // Setează datele principale
                setFormData({
                    auto: d.auto,
                    auto_type: d.auto_type,
                    driver: d.driver || "",
                    registration_number: d.registration_number,
                    VIN_series: d.VIN_series,
                    cylindrical_capacity: d.cylindrical_capacity,
                    combustibile: d.combustibile,
                    year: d.year ? d.year.split("-")[0] : "",

                    RCA: d.RCA || "",
                    RCA_display: d.RCA ? format(new Date(d.RCA), "dd.MM.yyyy") : "",

                    ITP: d.ITP || "",
                    ITP_display: d.ITP ? format(new Date(d.ITP), "dd.MM.yyyy") : "",

                    E_Vignette: d.E_Vignette || "",
                    E_Vignette_display: d.E_Vignette ? format(new Date(d.E_Vignette), "dd.MM.yyyy") : "",
                });

                // === KILOMETERS ===
                const vehicleKilometers = allKmResponse.data
                    .filter(km => km.vehicle === Number(id))
                    .sort((a, b) => {
                        // Sortează descrescător după created_at
                        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                        return dateB - dateA; // Cea mai recentă prima
                    });

                const latestKm = vehicleKilometers.length > 0 ? vehicleKilometers[0] : null;

                initialKilometersRef.current = latestKm ? {
                    kilometers: latestKm.kilometers?.toString() || "",
                    recorded_date: latestKm.recorded_date || format(new Date(), "yyyy-MM-dd"),
                } : {
                    kilometers: "",
                    recorded_date: format(new Date(), "yyyy-MM-dd"),
                };

                setKilometers({
                    kilometers: latestKm ? latestKm.kilometers?.toString() || "" : "",
                    recorded_date: latestKm ? latestKm.recorded_date || format(new Date(), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
                    recorded_date_display: latestKm ? format(new Date(latestKm.recorded_date), "dd.MM.yyyy") : format(new Date(), "dd.MM.yyyy"),
                });

                // === DISTRIBUTION ===
                const vehicleDistributions = allDistResponse.data
                    .filter(dist => dist.vehicle === Number(id))
                    .sort((a, b) => {
                        const dateA = a.distribution_data ? new Date(a.distribution_data) : new Date(0);
                        const dateB = b.distribution_data ? new Date(b.distribution_data) : new Date(0);
                        return dateB - dateA;
                    });

                const latestDistribution = vehicleDistributions.length > 0 ? vehicleDistributions[0] : null;

                initialDistributionRef.current = latestDistribution ? {
                    distribution_data: latestDistribution.distribution_data || "",
                    distribution_km: latestDistribution.distribution_km?.toString() || "",
                    distribution_interval: latestDistribution.distribution_interval?.toString() || "",
                    estimate_date_distribution: latestDistribution.estimate_date_distribution || "",
                    estimate_km_distribution: latestDistribution.estimate_km_distribution?.toString() || "",
                } : {
                    distribution_data: "",
                    distribution_km: "",
                    distribution_interval: "",
                    estimate_date_distribution: "",
                    estimate_km_distribution: "",
                };

                setDistribution(latestDistribution ? {
                    distribution_data: latestDistribution.distribution_data || "",
                    distribution_km: latestDistribution.distribution_km?.toString() || "",
                    distribution_interval: latestDistribution.distribution_interval?.toString() || "",
                    estimate_date_distribution: latestDistribution.estimate_date_distribution || "",
                    estimate_km_distribution: latestDistribution.estimate_km_distribution?.toString() || "",
                } : {
                    distribution_data: "",
                    distribution_km: "",
                    distribution_interval: "",
                    estimate_date_distribution: "",
                    estimate_km_distribution: "",
                });

                // === REVIEW ===
                const vehicleReviews = allRevResponse.data
                    .filter(rev => rev.vehicle === Number(id))
                    .sort((a, b) => {
                        const dateA = a.review_data ? new Date(a.review_data) : new Date(0);
                        const dateB = b.review_data ? new Date(b.review_data) : new Date(0);
                        return dateB - dateA;
                    });

                const latestReview = vehicleReviews.length > 0 ? vehicleReviews[0] : null;

                initialReviewRef.current = latestReview ? {
                    review_data: latestReview.review_data || "",
                    review_km: latestReview.review_km?.toString() || "",
                    review_interval: latestReview.review_interval?.toString() || "",
                    estimate_date_review: latestReview.estimate_date_review || "",
                    estimate_km_review: latestReview.estimate_km_review?.toString() || "",
                } : {
                    review_data: "",
                    review_km: "",
                    review_interval: "",
                    estimate_date_review: "",
                    estimate_km_review: "",
                };

                setReview(latestReview ? {
                    review_data: latestReview.review_data || "",
                    review_km: latestReview.review_km?.toString() || "",
                    review_interval: latestReview.review_interval?.toString() || "",
                    estimate_date_review: latestReview.estimate_date_review || "",
                    estimate_km_review: latestReview.estimate_km_review?.toString() || "",
                } : {
                    review_data: "",
                    review_km: "",
                    review_interval: "",
                    estimate_date_review: "",
                    estimate_km_review: "",
                });

                console.log("=== DATA LOADED SUCCESSFULLY FOR VIEW ===");

            } catch (e) {
                console.error("LOAD ERROR", e);
                console.error("Error details:", e.response?.data);
                showError("Error loading vehicle data. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    // Funcții pentru handleChange (la fel ca în EditAutovehicles)
    const handleDatePick = (field, date) => {
        if (!editMode) return;

        if (!date) {
            setFormData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setFormData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleKilometersDatePick = (date) => {
        if (!editMode) return;

        if (!date) {
            setKilometers((prev) => ({
                ...prev,
                recorded_date: "",
                recorded_date_display: ""
            }));

            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, recorded_date: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setKilometers((prev) => ({
            ...prev,
            recorded_date: iso,
            recorded_date_display: display,
        }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, recorded_date: "" }));
        }
    };

    const handleChange = (field) => (e) => {
        if (!editMode) return;

        let value = e.target.value;

        if (field === "year") value = value.replace(/\D/g, "").slice(0, 4);
        if (field === "cylindrical_capacity") value = value.replace(/\D/g, "");

        setFormData((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleKilometersChange = (field) => (e) => {
        if (!editMode) return;

        let value = e.target.value;

        if (field === "kilometers") {
            value = value.replace(/\D/g, "");
        }

        setKilometers((prev) => ({ ...prev, [field]: value }));

        if (isSubmitted && fieldErrors.kilometers) {
            setFieldErrors((prev) => ({ ...prev, kilometers: "" }));
        }
    };

    // Funcții pentru verificarea schimbărilor
    const hasValueChanged = (initial, current, fields) => {
        for (const field of fields) {
            const initialVal = initial[field] || "";
            const currentVal = current[field] || "";

            if (initialVal.toString() !== currentVal.toString()) {
                return true;
            }
        }
        return false;
    };

    const hasKilometersChanged = () => {
        const initial = initialKilometersRef.current || {};
        const current = {
            kilometers: kilometers.kilometers || "",
            recorded_date: kilometers.recorded_date || ""
        };

        return hasValueChanged(initial, current, ['kilometers', 'recorded_date']);
    };

    const hasDistributionChanged = () => {
        const initial = initialDistributionRef.current || {};
        const current = distribution;

        return hasValueChanged(initial, current, [
            'distribution_data',
            'distribution_km',
            'distribution_interval',
            'estimate_date_distribution',
            'estimate_km_distribution'
        ]);
    };

    const hasReviewChanged = () => {
        const initial = initialReviewRef.current || {};
        const current = review;

        return hasValueChanged(initial, current, [
            'review_data',
            'review_km',
            'review_interval',
            'estimate_date_review',
            'estimate_km_review'
        ]);
    };

    // Funcții de validare
    const validateMainForm = () => {
        const errors = {};

        const requiredFields = [
            "auto",
            "auto_type",
            "registration_number",
            "year",
            "VIN_series",
            "cylindrical_capacity",
            "RCA",
            "ITP",
            "E_Vignette",
            "combustibile",
        ];

        requiredFields.forEach((f) => {
            if (!formData[f] || formData[f].toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        if (!kilometers.kilometers || kilometers.kilometers.toString().trim() === "") {
            errors.kilometers = "Required";
        }

        if (!kilometers.recorded_date || kilometers.recorded_date.toString().trim() === "") {
            errors.recorded_date = "Required";
        }

        return errors;
    };

    const cleanPayload = (obj) => {
        const out = {};
        for (const key in obj) {
            out[key] = obj[key] === "" ? null : obj[key];
        }
        return out;
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
    };

    const shouldShowError = (fieldName, errorObj) => {
        return isSubmitted && errorObj[fieldName];
    };

    // Permisiuni
    const [canView, setCanView] = useState(false);
    const [canEditPermission, setCanEditPermission] = useState(false);

    useEffect(() => {
        const requiredPath = "/all-autovehicles";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const pages = storedPermissions?.pages ?? [];

        const checkPermission = (key) =>
            pages.some((p) => {
                try {
                    return new URL(p.url).pathname === requiredPath && p[key] === true;
                } catch {
                    return false;
                }
            });

        setCanView(checkPermission("can_view"));
        setCanEditPermission(checkPermission("can_edit"));
        // Eliminat canCreate deoarece nu este folosit
    }, []);

    // Handler pentru salvare
    const handleSave = async () => {
        if (!editMode) return;

        setIsSubmitted(true);

        const mainErrors = validateMainForm();
        setFieldErrors(mainErrors);

        const hasErrors = Object.keys(mainErrors).length > 0;

        if (hasErrors) {
            showError("Please fill in all required fields");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                ...formData,
                driver: formData.driver ? parseInt(formData.driver) : null,
                year: formData.year ? `${formData.year}-01-01` : null,
            };

            console.log("Saving vehicle payload:", payload);
            await axiosInstance.put(`/all-autovehicles/${id}/`, cleanPayload(payload));

            // Salvează kilometrajul doar dacă a fost modificat
            const kilometersChanged = hasKilometersChanged();

            if (kilometers.kilometers && kilometersChanged) {
                const kilometersPayload = {
                    ...cleanPayload(kilometers),
                    vehicle: parseInt(id),
                };

                console.log("Saving NEW kilometers record:", kilometersPayload);
                await axiosInstance.post("/autovehicle-kilometers/", kilometersPayload);
            }

            // Salvează distribuția doar dacă a fost modificată
            const distributionChanged = hasDistributionChanged();

            if (distributionChanged) {
                const hasDistributionData = Object.values(distribution).some(value =>
                    value && value.toString().trim() !== ""
                );

                if (hasDistributionData) {
                    const distributionPayload = {
                        ...cleanPayload(distribution),
                        vehicle: parseInt(id),
                    };

                    console.log("Saving NEW distribution record:", distributionPayload);
                    await axiosInstance.post("/distribution/", distributionPayload);
                }
            }

            // Salvează revizia doar dacă a fost modificată
            const reviewChanged = hasReviewChanged();

            if (reviewChanged) {
                const hasReviewData = Object.values(review).some(value =>
                    value && value.toString().trim() !== ""
                );

                if (hasReviewData) {
                    const reviewPayload = {
                        ...cleanPayload(review),
                        vehicle: parseInt(id),
                    };

                    console.log("Saving NEW review record:", reviewPayload);
                    await axiosInstance.post("/review/", reviewPayload);
                }
            }

            setShowToast(true);
            setEditMode(false);
            setIsSaving(false);
            setTimeout(() => setShowToast(false), 1500);

        } catch (e) {
            console.error("SAVE ERROR", e);
            console.error("Error details:", e.response?.data);
            setIsSaving(false);
            showError("Error saving vehicle. Please check the data and try again.");
        }
    };

    // Handler pentru anulare
    const handleCancel = () => {
        setEditMode(false);
        setIsSaving(false);
        setFieldErrors({});
        setIsSubmitted(false);
        // Reload data to reset form
        window.location.reload();
    };

    if (loading) return <div className="loading">Loading...</div>;

    if (!canView) {
        return (
            <div style={{ padding: "40px", textAlign: "center", fontSize: "18px" }}>
                ❌ You do not have permission to view this page.
            </div>
        );
    }

    return (
        <>
            {showToast && <div className="global-toast success">✔ Vehicle updated successfully!</div>}
            {showErrorToast && <div className="global-toast error">✗ {errorMessage}</div>}

            <div className="addautovehicles-page">
                <div className="add-autovehicle-container">

                    {/* HEADER */}
                    <div className="top-header addauto-header">
                        <div className="header-left">
                            <ArrowBackIcon className="back-icon-autovehicles" onClick={() => navigate("/all-autovehicles")} />
                            <h2 className="header-title-autovehicles">View autovehicle</h2>
                        </div>

                        <div className="header-actions">
                            {!editMode ? (
                                canEditPermission && (
                                    <button className="edit-btn" onClick={() => setEditMode(true)}>
                                        <EditIcon style={{ fontSize: "18px" }} />
                                        EDIT
                                    </button>
                                )
                            ) : (
                                <>
                                    <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                                    <button
                                        className={`save-btn ${isSaving ? "disabled" : ""}`}
                                        onClick={!isSaving ? handleSave : undefined}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <hr className="header-divider-autovehicles" />

                    {/* TABS */}
                    <div className="tab-buttons-autovehicles">
                        <button
                            className={`tab-btn ${window.location.pathname === `/all-autovehicles/view/${id}` ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}`)}
                        >
                            Details
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/contract-information') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/contract-information`)}
                        >
                            Contract Information
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/mileagehistory') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/mileagehistory`)}
                        >
                            Mileage History
                        </button>
                        <button
                            className={`tab-btn ${window.location.pathname.includes('/problems') ? "active" : ""}`}
                            onClick={() => navigate(`/all-autovehicles/view/${id}/problems`)}
                        >
                            Problems
                        </button>
                    </div>

                    {/* FORM SECTIONS - LA FEL CA ÎN EDIT */}
                    <div className="form-sections">

                        {/* LEFT SIDE */}
                        <div className="vehicle-form">
                            <div className="form-card">

                                <div className="row1">
                                    <div className="field">
                                        <label>Auto <span className="required">*</span></label>
                                        <input
                                            disabled={!editMode}
                                            value={formData.auto}
                                            placeholder="Auto name"
                                            onChange={handleChange("auto")}
                                            className={shouldShowError("auto", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto", fieldErrors) && <span className="field-error">{fieldErrors.auto}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Type <span className="required">*</span></label>
                                        <input
                                            disabled={!editMode}
                                            value={formData.auto_type}
                                            placeholder="Auto type"
                                            onChange={handleChange("auto_type")}
                                            className={shouldShowError("auto_type", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto_type", fieldErrors) && <span className="field-error">{fieldErrors.auto_type}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Registration number <span className="required">*</span></label>
                                        <input
                                            disabled={!editMode}
                                            value={formData.registration_number}
                                            placeholder="Ex: B 76 FGH"
                                            onChange={handleChange("registration_number")}
                                            className={shouldShowError("registration_number", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("registration_number", fieldErrors) && <span className="field-error">{fieldErrors.registration_number}</span>}
                                    </div>
                                </div>

                                <div className="row2">
                                    <div className="field">
                                        <label>Current Km <span className="required">*</span></label>
                                        <input
                                            disabled={!editMode}
                                            value={kilometers.kilometers}
                                            placeholder="Ex: 120000"
                                            onChange={handleKilometersChange("kilometers")}
                                            className={shouldShowError("kilometers", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("kilometers", fieldErrors) && <span className="field-error">{fieldErrors.kilometers}</span>}
                                    </div>

                                    <div className="field">
                                        <label>Km Recorded Date <span className="required">*</span></label>
                                        <div className="datepicker-container">
                                            <DatePicker
                                                id="datepicker-kilometers-date"
                                                selected={kilometers.recorded_date ? new Date(kilometers.recorded_date) : null}
                                                onChange={handleKilometersDatePick}
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="dd.mm.yyyy"
                                                className={`datepicker-input-with-icon ${shouldShowError("recorded_date", fieldErrors) ? "error-input" : ""}`}
                                                calendarClassName="custom-calendar"
                                                autoComplete="off"
                                                disabled={!editMode}
                                                readOnly={!editMode}
                                            />
                                            {editMode && (
                                                <span
                                                    className="calendar-icon-inside"
                                                    onClick={() => document.querySelector("#datepicker-kilometers-date")?.focus()}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                        <line x1="16" y1="2" x2="16" y2="6" />
                                                        <line x1="8" y1="2" x2="8" y2="6" />
                                                        <line x1="3" y1="10" x2="21" y2="10" />
                                                    </svg>
                                                </span>
                                            )}
                                        </div>
                                        {shouldShowError("recorded_date", fieldErrors) && <span className="field-error">{fieldErrors.recorded_date}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Driver</label>
                                        <Select
                                            value={selectedDriver}
                                            onChange={handleDriverSelect}
                                            options={driverOptions}
                                            placeholder="Select driver"
                                            isSearchable
                                            isClearable={true}
                                            styles={!formData.driver ? emptySelectStyles : customSelectStyles}
                                            className={`react-select-container ${shouldShowError("driver", fieldErrors) ? 'select-error' : ''}`}
                                            classNamePrefix="react-select"
                                            isDisabled={!editMode}
                                        />
                                        {shouldShowError("driver", fieldErrors) && <span className="field-error">{fieldErrors.driver}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field vin-full">
                                        <label>VIN series <span className="required">*</span></label>
                                        <input
                                            disabled={!editMode}
                                            value={formData.VIN_series}
                                            placeholder="Ex: TMBDC23U989016367"
                                            onChange={handleChange("VIN_series")}
                                            className={shouldShowError("VIN_series", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("VIN_series", fieldErrors) && <span className="field-error">{fieldErrors.VIN_series}</span>}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="right-side">
                            <div className="top-3-dates">

                                <div className="field">
                                    <label>Made year <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={formData.year}
                                        maxLength={4}
                                        placeholder="Enter year (YYYY)"
                                        onChange={handleChange("year")}
                                        className={shouldShowError("year", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("year", fieldErrors) && <span className="field-error">{fieldErrors.year}</span>}
                                </div>

                                <div className="field">
                                    <label>Cylindrical capacity <span className="required">*</span></label>
                                    <input
                                        disabled={!editMode}
                                        value={formData.cylindrical_capacity}
                                        placeholder="Engine capacity (cm³)"
                                        onChange={handleChange("cylindrical_capacity")}
                                        className={shouldShowError("cylindrical_capacity", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("cylindrical_capacity", fieldErrors) && <span className="field-error">{fieldErrors.cylindrical_capacity}</span>}
                                </div>

                                <div className="field">
                                    <label>Combustible <span className="required">*</span></label>
                                    <Select
                                        value={selectedFuel}
                                        onChange={handleFuelSelect}
                                        options={fuelOptions}
                                        placeholder="Select combustible type"
                                        isClearable={true}
                                        styles={shouldShowError("combustibile", fieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("combustibile", fieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                        isDisabled={!editMode}
                                    />
                                    {shouldShowError("combustibile", fieldErrors) && (
                                        <span className="field-error">{fieldErrors.combustibile}</span>
                                    )}
                                </div>

                                <div className="field">
                                    <label>RCA <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-rca"
                                            selected={formData.RCA ? new Date(formData.RCA) : null}
                                            onChange={(date) => handleDatePick("RCA", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("RCA", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-rca")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("RCA", fieldErrors) && <span className="field-error">{fieldErrors.RCA}</span>}
                                </div>

                                <div className="field">
                                    <label>ITP <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-itp"
                                            selected={formData.ITP ? new Date(formData.ITP) : null}
                                            onChange={(date) => handleDatePick("ITP", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("ITP", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-itp")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("ITP", fieldErrors) && <span className="field-error">{fieldErrors.ITP}</span>}
                                </div>

                                <div className="field">
                                    <label>E-Vignette <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-vignette"
                                            selected={formData.E_Vignette ? new Date(formData.E_Vignette) : null}
                                            onChange={(date) => handleDatePick("E_Vignette", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("E_Vignette", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                            disabled={!editMode}
                                            readOnly={!editMode}
                                        />
                                        {editMode && (
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-vignette")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    {shouldShowError("E_Vignette", fieldErrors) && <span className="field-error">{fieldErrors.E_Vignette}</span>}
                                </div>

                            </div>

                            <div className="bottom-forms">
                                <DistributionForm
                                    data={distribution}
                                    setData={setDistribution}
                                    editMode={editMode}
                                />
                                <ReviewForm
                                    data={review}
                                    setData={setReview}
                                    editMode={editMode}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default ViewAutovehicles;